import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  FilePlus2,
  Search,
  Building2,
  Mail,
  History,
  IndianRupee,
  Users,
  FileText,
  TrendingUp,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";
import { supabase } from "@/project/integrations/supabase/client";
import { Shell, PageHeader } from "@/project/components/Shell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { inr, shortDate } from "@/project/lib/format";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Dashboard · Assistance & Letter Desk" },
      {
        name: "description",
        content:
          "Daily overview of assistance applications, families assisted and recommendation letters generated.",
      },
      { property: "og:title", content: "Dashboard · Assistance & Letter Desk" },
      {
        property: "og:description",
        content:
          "Daily overview of assistance applications and recommendation letters.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: DashboardPage,
});

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => new Date().toISOString().slice(0, 8) + "01";

function DashboardPage() {
  const { data } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const t = today();
      const m = monthStart();
      const [apps, families, letters, recent] = await Promise.all([
        supabase
          .from("applications")
          .select(
            "id, application_date, status, total_requirement, our_contribution, assistance_categories(name)"
          ),
        supabase.from("families").select("id, blacklisted"),
        supabase
          .from("generated_letters")
          .select("id, generated_at, organization_name, requested_amount"),
        supabase
          .from("applications")
          .select(
            "id, ref_no, applicant_name, ration_card_no, total_requirement, application_date, status, assistance_categories(name)"
          )
          .order("created_at", { ascending: false })
          .limit(8),
      ]);
      const all = apps.data ?? [];
      const lets = letters.data ?? [];
      const fams = families.data ?? [];

      const sum = (
        rows: typeof all,
        key: "total_requirement" | "our_contribution"
      ) => rows.reduce((acc, r) => acc + Number(r[key] ?? 0), 0);

      const catMap = new Map<
        string,
        { name: string; count: number; amount: number }
      >();
      for (const a of all) {
        const name = a.assistance_categories?.name ?? "Uncategorised";
        const r = catMap.get(name) ?? { name, count: 0, amount: 0 };
        r.count += 1;
        r.amount += Number(a.total_requirement ?? 0);
        catMap.set(name, r);
      }

      const statusMap = new Map<string, number>();
      for (const a of all) {
        statusMap.set(a.status, (statusMap.get(a.status) ?? 0) + 1);
      }

      const orgMap = new Map<
        string,
        { name: string; letters: number; amount: number }
      >();
      for (const l of lets) {
        const name = l.organization_name ?? "—";
        const r = orgMap.get(name) ?? { name, letters: 0, amount: 0 };
        r.letters += 1;
        r.amount += Number(l.requested_amount ?? 0);
        orgMap.set(name, r);
      }

      return {
        total: all.length,
        today: all.filter(a => a.application_date === t).length,
        month: all.filter(a => a.application_date >= m).length,
        families: fams.length,
        blacklisted: fams.filter(f => f.blacklisted).length,
        requirement: sum(all, "total_requirement"),
        sanctioned: sum(all, "our_contribution"),
        lettersTotal: lets.length,
        lettersMonth: lets.filter(l => l.generated_at.slice(0, 10) >= m).length,
        byCategory: [...catMap.values()].sort((a, b) => b.count - a.count),
        byStatus: [...statusMap.entries()].sort((a, b) => b[1] - a[1]),
        byOrg: [...orgMap.values()]
          .sort((a, b) => b.letters - a.letters)
          .slice(0, 6),
        recent: recent.data ?? [],
      };
    },
  });

  const kpis: {
    label: string;
    value: string;
    hint: string;
    icon: LucideIcon;
    to: string;
    tone: string;
  }[] = [
    {
      label: "Total Applications",
      value: String(data?.total ?? 0),
      hint: `${data?.month ?? 0} this month · ${data?.today ?? 0} today`,
      icon: FileText,
      to: "/applications",
      tone: "chart-1",
    },
    {
      label: "Members Assisted",
      value: String(data?.families ?? 0),
      hint: `${data?.blacklisted ?? 0} blacklisted`,
      icon: Users,
      to: "/members",
      tone: "chart-2",
    },
    {
      label: "Total Requirement",
      value: inr(data?.requirement ?? 0),
      hint: "Across all applications",
      icon: IndianRupee,
      to: "/reports",
      tone: "chart-3",
    },
    {
      label: "Trust Contribution",
      value: inr(data?.sanctioned ?? 0),
      hint: "Sanctioned by the trust",
      icon: TrendingUp,
      to: "/reports",
      tone: "success",
    },
    {
      label: "Letters Issued",
      value: String(data?.lettersTotal ?? 0),
      hint: `${data?.lettersMonth ?? 0} this month`,
      icon: Mail,
      to: "/letters",
      tone: "chart-5",
    },
  ];

  const quickActions: {
    to: string;
    icon: LucideIcon;
    label: string;
    tone: string;
  }[] = [
    {
      to: "/applications/new",
      icon: FilePlus2,
      label: "New Sadharmik",
      tone: "chart-1",
    },
    {
      to: "/applications",
      icon: Search,
      label: "Search Applications",
      tone: "chart-2",
    },
    { to: "/letters", icon: Mail, label: "Letter History", tone: "chart-3" },
    {
      to: "/organizations",
      icon: Building2,
      label: "Organization Master",
      tone: "chart-4",
    },
    { to: "/members", icon: Users, label: "Member Master", tone: "chart-5" },
    { to: "/reports", icon: History, label: "Reports", tone: "success" },
  ];

  const maxCat = Math.max(1, ...(data?.byCategory ?? []).map(c => c.count));
  const maxOrg = Math.max(1, ...(data?.byOrg ?? []).map(o => o.letters));

  return (
    <Shell>
      <PageHeader
        title="Assistance Desk Dashboard"
        subtitle="Live overview across applications, members and recommendation letters"
        actions={
          <Button asChild size="lg">
            <Link to="/applications/new">
              <FilePlus2 className="mr-2 h-4 w-4" /> New Application
            </Link>
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {kpis.map(k => (
          <Link key={k.label} to={k.to} className="group">
            <Card
              className="h-full overflow-hidden border-0 shadow-sm transition-all group-hover:-translate-y-0.5 group-hover:shadow-lg"
              style={{
                background: `linear-gradient(135deg, color-mix(in oklab, var(--${k.tone}) 16%, var(--card)), var(--card))`,
                borderTop: `3px solid var(--${k.tone})`,
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {k.label}
                  </p>
                  <span
                    className="rounded-lg p-2"
                    style={{
                      background: `color-mix(in oklab, var(--${k.tone}) 22%, transparent)`,
                      color: `var(--${k.tone})`,
                    }}
                  >
                    <k.icon className="h-4 w-4" />
                  </span>
                </div>
                <p
                  className="font-display mt-2 text-2xl font-semibold"
                  style={{ color: `var(--${k.tone})` }}
                >
                  {k.value}
                </p>
                <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                  {k.hint}
                  <ArrowRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                </p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {quickActions.map(a => (
          <Link key={a.label} to={a.to} className="group">
            <Card
              className="h-full border-0 shadow-sm transition-all group-hover:-translate-y-0.5 group-hover:shadow-md"
              style={{
                background: `color-mix(in oklab, var(--${a.tone}) 12%, var(--card))`,
              }}
            >
              <CardContent className="flex flex-col items-start gap-2 p-4">
                <span
                  className="rounded-lg p-2"
                  style={{
                    background: `color-mix(in oklab, var(--${a.tone}) 24%, transparent)`,
                    color: `var(--${a.tone})`,
                  }}
                >
                  <a.icon className="h-4 w-4" />
                </span>
                <span className="text-sm font-medium leading-tight">
                  {a.label}
                </span>
                <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  Open <ArrowRight className="h-3 w-3" />
                </span>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Applications by Category
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data?.byCategory ?? []).map(c => (
              <div key={c.name}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">{c.name}</span>
                  <span className="text-muted-foreground">
                    {c.count} · {inr(c.amount)}
                  </span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${(c.count / maxCat) * 100}%` }}
                  />
                </div>
              </div>
            ))}
            {!data?.byCategory?.length && (
              <p className="text-sm text-muted-foreground">
                No applications yet.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Status Overview</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            {(data?.byStatus ?? []).map(([name, value]) => (
              <div
                key={name}
                className="hover:bg-muted/50 rounded-lg border border-border p-3 transition-colors"
              >
                <p className="font-display text-xl font-semibold">{value}</p>
                <p className="text-xs text-muted-foreground">{name}</p>
              </div>
            ))}
            {!data?.byStatus?.length && (
              <p className="text-sm text-muted-foreground">
                No status data yet.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top Institutions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data?.byOrg ?? []).map(o => (
              <div key={o.name}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="truncate font-medium">{o.name}</span>
                  <span className="text-muted-foreground">
                    {o.letters} letter(s) · {inr(o.amount)}
                  </span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-accent"
                    style={{ width: `${(o.letters / maxOrg) * 100}%` }}
                  />
                </div>
              </div>
            ))}
            {!data?.byOrg?.length && (
              <p className="text-sm text-muted-foreground">
                No letters issued yet.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-4 grid gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Recent Applications</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <table className="data-table w-full text-sm">
              <thead>
                <tr>
                  <th className="px-4 py-2">Ref</th>
                  <th className="px-4 py-2">Applicant</th>
                  <th className="px-4 py-2">Ration Card</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2 text-right">Requirement</th>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent ?? []).map(r => (
                  <tr key={r.id} className="hover:bg-muted/40">
                    <td className="px-4 py-2 font-medium">
                      <Link
                        to="/applications/$id"
                        params={{ id: r.id }}
                        className="underline"
                      >
                        {r.ref_no}
                      </Link>
                    </td>
                    <td className="px-4 py-2">{r.applicant_name}</td>
                    <td className="px-4 py-2">{r.ration_card_no}</td>
                    <td className="px-4 py-2">
                      {r.assistance_categories?.name}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {inr(r.total_requirement)}
                    </td>
                    <td className="px-4 py-2">
                      {shortDate(r.application_date)}
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant="secondary">{r.status}</Badge>
                    </td>
                  </tr>
                ))}
                {!data?.recent?.length && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      No applications yet. Start with “New Application”.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </Shell>
  );
}
